import _extends from "@babel/runtime/helpers/extends";
import React, { useState } from "react";
import { Alert, Badge, Box, Button, Card, Grid, Icon, Picture, Spinner, Text } from "~/components-shared";
import styled, { useTheme } from "styled-components";
import { AuthoritiesEnum, IntegrationTypeEnum, StatusStateEnum } from "~/types/enums";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import lists from "~/lib/lists";
import { TrendReportActionEnum } from "~/store/trendReports/types";
import { usePostHog } from "posthog-js/react";
import trendReportsActionCreators from "~/store/trendReports/actions";
import toastsActionCreators from "~/store/toasts/actions";
import trendReportSelectors from "~/store/trendReports/selectors";
import useAbacExt from "~/hooks/abac";
import integrationSelectors from "~/store/integrations/selectors";
import { helpers } from "~/lib/helpers";
import Dropdown from "../../components-shared/Dropdown";
const Actions = styled(Box)`
  margin: -10px -10px 0 5px;
  float: right;
`;
const Description = styled(Text)`
  flex-grow: 1;
  width: 100%;
  overflow: auto;
`;
const StyledSource = styled(Picture)`
  background-color: ${_ref => {
  let {
    theme
  } = _ref;
  return theme.colors.background.primary;
}};
  background-size: 80%;
`;
const Contributions = styled.div`
  background: ${_ref2 => {
  let {
    theme
  } = _ref2;
  return theme.colors.primary.light;
}};
  width: calc(100% + 2.8em);
  padding: 1em 1em 1.5em;
  text-align: center;
  margin: 0 -1.4em;
  border-bottom-left-radius: ${_ref3 => {
  let {
    theme
  } = _ref3;
  return theme.shape.borderRadius.primary;
}};
  border-bottom-right-radius: ${_ref4 => {
  let {
    theme
  } = _ref4;
  return theme.shape.borderRadius.primary;
}};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;
const Cap = styled.div`
  display: inline-block;
  width: ${_ref5 => {
  let {
    theme
  } = _ref5;
  return 6 * theme.typography.fontStep;
}}rem;
  min-width: ${_ref6 => {
  let {
    theme
  } = _ref6;
  return 6 * theme.typography.fontStep;
}}rem;
  height: ${_ref7 => {
  let {
    theme
  } = _ref7;
  return 6 * theme.typography.fontStep;
}}rem;
  text-align: center;
  line-height: ${_ref8 => {
  let {
    theme
  } = _ref8;
  return 6 * theme.typography.fontStep;
}}rem;
  border-radius: 50%;
  background-color: ${_ref9 => {
  let {
    theme
  } = _ref9;
  return theme.colors.primary.light;
}};
  position: relative;
  font-weight: ${_ref10 => {
  let {
    theme
  } = _ref10;
  return theme.typography.fontWeight.semiBold;
}};
  font-size: ${_ref11 => {
  let {
    theme
  } = _ref11;
  return 3 * theme.typography.fontStep;
}}rem;
  color: ${_ref12 => {
  let {
    theme
  } = _ref12;
  return theme.colors.primary.main;
}};
  box-shadow: 0 0 0 2px ${_ref13 => {
  let {
    theme
  } = _ref13;
  return theme.colors.background.primary;
}};
`;
const CustomerTrendingTopicCard = _ref14 => {
  let {
    reportId,
    trend,
    hideActions = false,
    ...rest
  } = _ref14;
  const {
    t
  } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const followStatus = useSelector(trendReportSelectors.getStatusByTrendFollow(trend.id));
  const followError = useSelector(trendReportSelectors.getErrorByTrendFollow(trend.id));
  const markStatus = useSelector(trendReportSelectors.getStatusByMarkForInsights(trend.id));
  const markError = useSelector(trendReportSelectors.getErrorByMarkForInsights(trend.id));
  const [hoverFollow, setHoverFollow] = useState(false);
  const error = followError || markError;
  const {
    userHas
  } = useAbacExt();
  const sendStatus = useSelector(trendReportSelectors.getStatusByTrendSend(trend.id));
  const slackIntegration = useSelector(integrationSelectors.getIntegrationByType(IntegrationTypeEnum.SLACK));
  const style = trend.styles?.[0];
  const posthog = usePostHog();
  const handleFollowClick = () => {
    if (trend.theme.topic?.isFollowed) {
      dispatch(trendReportsActionCreators.unfollowTrendStarted(reportId, trend.id));
      posthog.capture(`Topic Trend – Unfollow "${trend.theme.name}"`, {
        reportId,
        trendId: trend.id
      });
    } else {
      dispatch(trendReportsActionCreators.followTrendStarted(reportId, trend.id, _ref15 => {
        let {
          data
        } = _ref15;
        if (data) {
          dispatch(toastsActionCreators.pushed({
            severity: "success",
            message: t("TopicTrendCard.successfullyFollowed"),
            timeout: 10000
          }));
        }
      }));
      posthog.capture(`Topic Trend – Follow "${trend.theme.name}"`, {
        reportId,
        trendId: trend.id
      });
    }
  };
  const handleDelete = () => {
    dispatch(trendReportsActionCreators.actionChanged(TrendReportActionEnum.DELETE_TREND, reportId, trend.id));
    posthog.capture(`Topic Trend – Delete "${trend.theme.name}"`, {
      reportId,
      trendId: trend.id
    });
  };
  const handleDismiss = () => {
    dispatch(trendReportsActionCreators.actionChanged(TrendReportActionEnum.DISMISS_TREND, reportId, trend.id));
    posthog.capture(`Topic Trend – Dismiss "${trend.theme.name}"`, {
      reportId,
      trendId: trend.id
    });
  };
  const handleShareInSlack = () => {
    dispatch(trendReportsActionCreators.actionChanged(TrendReportActionEnum.SHARE_TREND_IN_SLACK, reportId, trend.id));
    posthog.capture(`Topic Trend – Share in Slack "${trend.theme.name}"`, {
      reportId,
      trendId: trend.id
    });
  };
  const handleSendInSlack = callback => {
    dispatch(trendReportsActionCreators.sendTrendSlackStarted(reportId, trend.id, _ref16 => {
      let {
        data
      } = _ref16;
      if (data === trend.id) {
        dispatch(toastsActionCreators.pushed({
          severity: "success",
          message: t("TrendingTopicCard.reportSent"),
          timeout: 10000
        }));
        callback();
      }
    }));
    posthog.capture(`Topic Trend – Send in Slack "${trend.theme.name}"`, {
      reportId,
      trendId: trend.id
    });
  };
  const handleOverview = () => {
    dispatch(trendReportsActionCreators.actionChanged(TrendReportActionEnum.OVERVIEW, reportId, trend.id));
    posthog.capture(`Topic Trend – Overview "${trend.theme.name}"`, {
      reportId,
      trendId: trend.id
    });
  };
  const handleToggleSelectForInsights = () => {
    if (trend.isCustomerRelated) {
      dispatch(trendReportsActionCreators.unmarkForInsightsStarted(reportId, trend.id, _ref17 => {
        let {
          data
        } = _ref17;
        if (data) {
          dispatch(toastsActionCreators.pushed({
            severity: "success",
            message: t("TrendingTopicCard.successfullyUnmarkedForInsights", {
              name: style?.name || trend.theme.name
            }),
            timeout: 10000
          }));
        }
      }));
    } else {
      dispatch(trendReportsActionCreators.markForInsightsStarted(reportId, trend.id, _ref18 => {
        let {
          data
        } = _ref18;
        if (data) {
          dispatch(toastsActionCreators.pushed({
            severity: "success",
            message: t("TrendingTopicCard.successfullyMarkedForInsights", {
              name: style?.name || trend.theme.name
            }),
            timeout: 10000
          }));
        }
      }));
    }
    posthog.capture(`Topic Trend – Select for Insights "${trend.theme.name}"`, {
      reportId,
      trendId: trend.id
    });
  };
  const descArray = helpers.splitSentences(style?.description || trend.theme.description || "");
  return /*#__PURE__*/React.createElement(Card, _extends({
    s: {
      p: 5,
      pb: 0
    },
    align: "left",
    minHeight: "420px",
    onClick: handleOverview,
    color: "background",
    shadow: true
  }, rest), /*#__PURE__*/React.createElement(Text, {
    element: "h4",
    s: {
      mb: 4
    },
    textOverflow: true
  }, /*#__PURE__*/React.createElement(Actions, {
    align: "center"
  }, /*#__PURE__*/React.createElement(Dropdown, {
    direction: "bottomLeft",
    handlerProps: {
      s: {
        p: 2
      },
      "data-tooltip-id": "general-tooltip",
      "data-tooltip-content": t("common.actions.options")
    },
    handler: /*#__PURE__*/React.createElement(Icon, {
      src: "dots-vertical",
      color: theme.colors.text.primary,
      display: "block"
    })
  }, _ref19 => {
    let {
      isOpen,
      onItemClick
    } = _ref19;
    return /*#__PURE__*/React.createElement(Dropdown.List, {
      isOpen: isOpen
    }, !!slackIntegration && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: e => {
        e.stopPropagation();
        handleSendInSlack(() => {
          onItemClick();
        });
      }
    }, sendStatus === StatusStateEnum.LOADING ? /*#__PURE__*/React.createElement(Spinner, {
      scale: 4.5,
      s: {
        mr: 2
      },
      color: "primary"
    }) : /*#__PURE__*/React.createElement(Icon, {
      scale: 4.5,
      src: "send",
      s: {
        mr: 2
      },
      color: "primary"
    }), t("TopicInfoCard.sendToSlack")), !!slackIntegration && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: e => {
        e.stopPropagation();
        handleShareInSlack();
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      scale: 4.5,
      src: "share",
      s: {
        mr: 2
      },
      color: "primary"
    }), t("TopicInfoCard.shareInSlack")), /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: e => {
        e.stopPropagation();
        handleDismiss();
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      src: "cross",
      scale: 4.5,
      s: {
        mr: 2
      },
      color: "primary"
    }), t("common.actions.dismiss"), /*#__PURE__*/React.createElement(Icon, {
      s: {
        ml: 1
      },
      color: "primary",
      "data-tooltip-id": "general-tooltip",
      "data-tooltip-html": "<strong>Hide</strong> Trending topic from your report",
      src: "infoOutline",
      style: {
        cursor: "help"
      }
    })), userHas([AuthoritiesEnum.EXPERIMENTAL]) && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: e => {
        e.stopPropagation();
        handleToggleSelectForInsights();
        onItemClick();
      }
    }, markStatus === StatusStateEnum.LOADING ? /*#__PURE__*/React.createElement(Spinner, {
      scale: 4,
      color: "primary",
      s: {
        mr: 2
      }
    }) : /*#__PURE__*/React.createElement(Icon, {
      src: trend.isCustomerRelated ? "bookmarkCheckFill" : "bookmark",
      scale: 4.5,
      s: {
        mr: 2
      },
      color: "primary"
    }), trend.isCustomerRelated ? t("TrendingTopicCard.unmarkForInsights") : t("TrendingTopicCard.markForInsights"), /*#__PURE__*/React.createElement(Icon, {
      src: "encrypted",
      color: "primary",
      s: {
        ml: 1
      }
    })), /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: e => {
        e.stopPropagation();
        handleDelete();
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      src: "bin",
      scale: 4.5,
      s: {
        mr: 2
      },
      color: "danger"
    }), t("common.actions.delete"), /*#__PURE__*/React.createElement(Icon, {
      src: "encrypted",
      color: "primary",
      s: {
        ml: 1
      }
    }))));
  })), /*#__PURE__*/React.createElement(Box, {
    gap: 1,
    s: {
      mb: 4,
      mx: -2
    }
  }, trend.isCustomerRelated && /*#__PURE__*/React.createElement(Badge, {
    lineHeight: 4,
    color: "translucentPrimary",
    s: {
      px: 2,
      py: 1
    },
    rounded: true
  }, "Customer"), /*#__PURE__*/React.createElement(Badge, {
    fontSize: 3,
    lineHeight: 4,
    color: lists.trends.workTypes.genericColors[trend.workType],
    s: {
      px: 3,
      py: 1
    },
    rounded: true
  }, lists.trends.workTypes.names[trend.workType])), style?.name || trend.theme.name), /*#__PURE__*/React.createElement(Description, {
    element: "p",
    s: {
      mb: 4
    },
    color: "textSecondary",
    textOverflow: true
  }, descArray[0]), /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    gap: 3,
    template: "auto auto",
    justify: "space-between",
    fullWidth: true,
    s: {
      mb: 4
    }
  }, trend.revenue?.total ? /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    fontSize: 3,
    color: "textSecondary",
    display: "block",
    weight: "regular",
    s: {
      mb: 2
    }
  }, t("TopicTrendCard.revenue")), /*#__PURE__*/React.createElement(Badge, {
    color: "primaryAccentContrast",
    display: "inline",
    fontSize: 3.5,
    weight: "semiBold",
    s: {
      py: 1,
      px: 3
    }
  }, trend.revenue?.total)) : /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    fontSize: 3,
    color: "textSecondary",
    display: "block",
    weight: "regular",
    s: {
      mb: 2
    }
  }, t("TopicTrendCard.contributors")), /*#__PURE__*/React.createElement(Box, {
    align: "center"
  }, trend.contributors.length ? /*#__PURE__*/React.createElement(Text, {
    fontSize: 4.5,
    weight: "medium",
    color: "text"
  }, trend.contributors.length) : /*#__PURE__*/React.createElement(Text, null, "-"))), /*#__PURE__*/React.createElement(Grid.Cell, {
    justify: "end",
    style: {
      textAlign: "right"
    }
  }, /*#__PURE__*/React.createElement(Text, {
    fontSize: 3,
    color: "textSecondary",
    display: "block",
    s: {
      mb: 2
    }
  }, "Sources"), /*#__PURE__*/React.createElement(Box, {
    align: "center",
    justify: "end"
  }, trend.sources.slice(0, 3).map(source => /*#__PURE__*/React.createElement(StyledSource, {
    key: source,
    s: {
      mx: -1
    },
    width: "1.8rem",
    height: "1.8rem",
    border: "rounded",
    src: lists.common.apps.logos(theme)[source]
  })), trend.sources.length > 3 && /*#__PURE__*/React.createElement(Cap, null, "+", trend.sources.length - 3)))), /*#__PURE__*/React.createElement(Contributions, null, error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, error), /*#__PURE__*/React.createElement(Text, {
    color: theme.colors.primary.main,
    fontSize: 3,
    weight: "semiBold",
    display: "block"
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "union",
    s: {
      mr: 1.5,
      mt: -0.5
    },
    color: theme.colors.primary.main
  }), trend.footprintCount, " contributions"), !hideActions && /*#__PURE__*/React.createElement(Button, {
    onClick: e => {
      e.stopPropagation();
      handleFollowClick();
    },
    onMouseEnter: () => setHoverFollow(true),
    onMouseLeave: () => setHoverFollow(false),
    color: trend.theme.topic?.isFollowed ? "translucent" : "primary",
    display: "block",
    fontSize: 3.5,
    loading: followStatus === StatusStateEnum.LOADING,
    s: {
      py: 2,
      pl: 4,
      pr: trend.theme.topic?.isFollowed ? 7 : 4,
      mt: 3
    },
    outline: false,
    maxWidth: "150px",
    textAlign: "center",
    fullWidth: true
  }, trend.theme.topic?.isFollowed && /*#__PURE__*/React.createElement(Icon, {
    src: "tick",
    s: {
      mr: 2
    }
  }), trend.theme.topic?.isFollowed ? hoverFollow ? t("TopicTrendCard.unfollow") : t("TopicTrendCard.following") : t("TopicTrendCard.follow"))));
};
export default CustomerTrendingTopicCard;